import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Avatar,
  Badge,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Autocomplete,
  TextField,
  Paper,
} from "@mui/material";
import {
  Search,
  Notifications,
  Menu as MenuIcon,
  Timer,
  Person,
  Business,
  People,
  Logout,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/store/authStore";
import { authService } from "@/services/auth.service";
import { customerService, type Customer } from "@/services/customer.service";
import { formatCustomerName } from "@/utils/formatCustomerName";
import { getBusinessNow } from "@/utils/businessTime";
import { styled } from "@mui/material/styles";

const SearchBox = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: "100%",
  maxWidth: 384,
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(3),
    width: "auto",
  },
  "& .MuiAutocomplete-root": {
    width: "100%",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.mode === "light" ? "#f3f4f6" : "#1f2937",
    fontSize: "14px",
    "&:hover": {
      backgroundColor: theme.palette.mode === "light" ? "#e5e7eb" : "#374151",
    },
    "&.Mui-focused": {
      backgroundColor: theme.palette.mode === "light" ? "#e5e7eb" : "#374151",
    },
    [theme.breakpoints.up("md")]: {
      minWidth: "20ch",
      "&.Mui-focused": {
        minWidth: "30ch",
      },
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
}));

interface TopBarProps {
  onMenuClick?: () => void;
}

/**
 * TopBar avec recherche, notifications et profil
 * Basé sur la maquette défi_gourmand_admin_dashboard_1
 */
export function TopBar({ onMenuClick }: TopBarProps): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);

  const { data: customers = [] } = useQuery({
    queryKey: ["customers", "topbar-search"],
    queryFn: () => customerService.getAll(),
    enabled: searchOpen && user?.role !== "DRIVER",
    staleTime: 60_000,
  });

  const customerOptions = useMemo(() => customers, [customers]);

  const openProfileMenu = (e: React.MouseEvent<HTMLElement>) =>
    setProfileAnchor(e.currentTarget);
  const closeProfileMenu = () => setProfileAnchor(null);

  const handleMonCompte = () => {
    closeProfileMenu();
    navigate("/account");
  };

  const handleMonEntreprise = () => {
    closeProfileMenu();
    navigate("/society");
  };

  const handleMesUtilisateurs = () => {
    closeProfileMenu();
    navigate("/users");
  };

  const handleLogout = async () => {
    closeProfileMenu();
    await authService.logout();
    useAuthStore.getState().clearAuth();
    navigate("/login");
  };

  // Timer d’alerte deadline : temps restant jusqu’à 10h00 (ou « Dépassée » si passé)
  const DEADLINE_HOUR = 10;
  const DEADLINE_MINUTE = 0;
  const [timeRemaining, setTimeRemaining] = useState("--:--:--");
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);

  useEffect(() => {
    const tick = () => {
      // Compte à rebours en heure Guyane : la deadline 10h est exprimée en local Guyane.
      const now = getBusinessNow();
      const deadline = new Date(now);
      deadline.setHours(DEADLINE_HOUR, DEADLINE_MINUTE, 0, 0);
      const diff = deadline.getTime() - now.getTime();
      if (diff <= 0) {
        setIsDeadlinePassed(true);
        setTimeRemaining("Dépassée");
        return;
      }
      setIsDeadlinePassed(false);
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeRemaining(
        `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
      );
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        height: 64, // h-16
        bgcolor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        color: "text.primary",
      }}
    >
      <Toolbar sx={{ height: "100%", px: 3 }}>
        {/* Mobile Menu Button */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={onMenuClick}
          sx={{ mr: 2, display: { md: "none" } }}
        >
          <MenuIcon />
        </IconButton>

        {/* Recherche client (masquée pour le rôle DRIVER) */}
        {user?.role !== "DRIVER" && (
          <SearchBox sx={{ display: { xs: "none", md: "block" } }}>
            <Autocomplete
              options={customerOptions}
              getOptionLabel={(option: Customer) =>
                [formatCustomerName(option), option.email].filter(Boolean).join(" — ")
              }
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.trim().toLowerCase();
                if (!q) return options.slice(0, 15);
                const qNorm = q.replace(/[\s.\-]/g, "");
                const matchPhone = (phone: string | null | undefined) => {
                  if (!phone || !qNorm) return false;
                  const phoneNorm = phone.replace(/[\s.\-]/g, "");
                  return phoneNorm.includes(qNorm);
                };
                return options
                  .filter(
                    (c) =>
                      formatCustomerName(c).toLowerCase().includes(q) ||
                      (c.firstName?.toLowerCase().includes(q) ?? false) ||
                      (c.lastName?.toLowerCase().includes(q) ?? false) ||
                      (c.email?.toLowerCase().includes(q) ?? false) ||
                      matchPhone(c.phone)
                  )
                  .slice(0, 15);
              }}
              onOpen={() => setSearchOpen(true)}
              onClose={() => setSearchOpen(false)}
              onChange={(_event, value: Customer | null) => {
                if (value?.id) {
                  navigate(`/customers/${value.id}`);
                  setSearchInputValue("");
                  setSearchOpen(false);
                }
              }}
              onInputChange={(_event, newInputValue) => {
                setSearchInputValue(newInputValue);
              }}
              value={null}
              inputValue={searchInputValue}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Rechercher un client..."
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Box
                          component="span"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pl: 1.5,
                            pr: 0.5,
                            color: "text.secondary",
                          }}
                        >
                          <Search fontSize="small" />
                        </Box>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {formatCustomerName(option)}
                    </Typography>
                    {(option.email || option.phone) && (
                      <Typography variant="caption" color="text.secondary">
                        {[option.email, option.phone].filter(Boolean).join(" · ")}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              PaperComponent={(props) => (
                <Paper {...props} elevation={8} sx={{ mt: 1.5 }} />
              )}
              noOptionsText="Aucun client trouvé"
              loadingText="Chargement…"
              aria-label="Rechercher un client"
            />
          </SearchBox>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* Right Actions */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: { xs: 2, md: 3 } }}
        >
          {/* Deadline Timer Widget — alerte jusqu’à 10h00 */}
          {user?.role !== "DRIVER" && (
            <Box
              component="span"
              title="Heure limite pour valider les commandes du jour"
              sx={{
                display: { xs: "none", lg: "flex" },
                alignItems: "center",
                gap: 1.25,
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: (theme) =>
                  isDeadlinePassed
                    ? theme.palette.action.hover
                    : theme.palette.mode === "light"
                      ? "error.50"
                      : "rgba(211, 47, 47, 0.12)",
                border: "1px solid",
                borderColor: (theme) =>
                  isDeadlinePassed
                    ? theme.palette.divider
                    : theme.palette.error.main + "40",
                transition: "border-color 0.2s, background-color 0.2s",
              }}
            >
              <Timer
                sx={{
                  fontSize: 22,
                  color: isDeadlinePassed ? "text.secondary" : "error.main",
                  flexShrink: 0,
                  ...(isDeadlinePassed
                    ? {}
                    : {
                        "@keyframes deadline-pulse": {
                          "0%, 100%": { opacity: 1 },
                          "50%": { opacity: 0.6 },
                        },
                        animation: "deadline-pulse 2s ease-in-out infinite",
                      }),
                }}
              />
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "10px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "text.secondary",
                    lineHeight: 1.2,
                  }}
                >
                  Commandes du jour
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    fontWeight: 700,
                    fontSize: "1rem",
                    lineHeight: 1.2,
                    color: isDeadlinePassed ? "text.secondary" : "error.main",
                  }}
                >
                  {isDeadlinePassed ? "Dépassée" : timeRemaining}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Notifications */}
          <IconButton color="inherit" sx={{ position: "relative" }}>
            <Badge
              badgeContent={1}
              color="primary"
              sx={{
                "& .MuiBadge-badge": {
                  width: 8,
                  height: 8,
                  minWidth: 8,
                  padding: 0,
                  border: 2,
                  borderColor: "background.paper",
                },
              }}
            >
              <Notifications />
            </Badge>
          </IconButton>

          {/* Profile Avatar + Menu */}
          <IconButton
            onClick={openProfileMenu}
            aria-label="Mon compte"
            aria-controls={profileAnchor ? "profile-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={profileAnchor ? "true" : undefined}
            sx={{ p: 0.5 }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: "primary.main",
              }}
              alt={user?.firstName || "User"}
            >
              {user?.firstName?.[0]?.toUpperCase() || "U"}
            </Avatar>
          </IconButton>
          <Menu
            id="profile-menu"
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={closeProfileMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { mt: 2, minWidth: 200 } }}
          >
            <MenuItem onClick={handleMonCompte}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              <ListItemText>Mon compte</ListItemText>
            </MenuItem>
            {user?.role === "ADMIN" && (
              <MenuItem onClick={handleMonEntreprise}>
                <ListItemIcon>
                  <Business fontSize="small" />
                </ListItemIcon>
                <ListItemText>Mon entreprise</ListItemText>
              </MenuItem>
            )}
            {user?.role !== "DRIVER" && (
              <MenuItem onClick={handleMesUtilisateurs}>
                <ListItemIcon>
                  <People fontSize="small" />
                </ListItemIcon>
                <ListItemText>Mes Utilisateurs</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
              <ListItemIcon sx={{ color: "inherit" }}>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Déconnexion</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
